import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import '../styles/ServiceTransformCards.css';

const services = [
  {
    id: 'record-review',
    tag: 'Foundation',
    title: 'Diagnostic Record Review',
    typical: [
      "Records sit in a folder, unread",
      "Patterns missed across visits",
      "No one connects the dots"
    ],
    promise: [
      "Every page reviewed with fresh eyes",
      "Patterns identified and connected",
      "Clear summary of what to ask next"
    ]
  },
  {
    id: 'advocacy-launch',
    tag: 'Clarity & Confidence',
    title: 'Advocacy Launch Session',
    typical: [
      "Mind goes blank at appointments",
      "Leave with more questions than answers",
      "Symptoms dismissed or minimized"
    ],
    promise: [
      "One-page script of your story",
      "Practice until you're confident",
      "Doctor listens and takes action"
    ]
  },
  {
    id: 'deep-dive',
    tag: 'The Full Picture',
    title: 'Deep Dive Consultation',
    typical: [
      "Multiple specialists, no coordination",
      "Exhausted repeating your story",
      "No one sees the whole puzzle"
    ],
    promise: [
      "Two hours gathering every detail",
      "All records connected and analyzed",
      "Single coordinated care plan"
    ]
  },
  {
    id: 'membership',
    tag: 'Ongoing Partnership',
    title: 'Membership Plans',
    typical: [
      "Navigate health alone",
      "Guess when to call a doctor",
      "Wait weeks just to get a callback"
    ],
    promise: [
      "Partner who knows your history",
      "Regular check-ins and email access",
      "Support before every important visit"
    ],
    link: '/membership'
  },
  {
    id: 'appointment-prep',
    tag: 'Show Up Ready',
    title: 'Appointment Preparation',
    typical: [
      "Forget key questions",
      "Doctor seems rushed",
      "Leave without answers"
    ],
    promise: [
      "One-page prep sheet 48hrs before",
      "Top questions prioritized",
      "Walk in clear, leave with answers"
    ]
  },
  {
    id: 'provider-coord',
    tag: 'Stop the Phone Tag',
    title: 'Provider Coordination',
    typical: [
      "Hours on hold for records",
      "Front desk can't answer questions",
      "Weeks pass without progress"
    ],
    promise: [
      "We make the calls for you",
      "Records requested and tracked",
      "You focus on healing while we handle the system"
    ]
  }
];

// Export services data for use in other components
export { services };

const ServiceTransformCards = ({ filterIds = null, defaultExpanded = false }) => {
  // Track which rows are open. Set of ids — visitor can open many at once
  // to compare apples-to-apples across packages without losing context.
  // When `defaultExpanded` is true (used on the Membership page), all rows
  // start open so the comparison reads as a static table at first glance.
  const displayServicesInit = filterIds
    ? services.filter(s => filterIds.includes(s.id))
    : services;
  const [openIds, setOpenIds] = useState(() =>
    defaultExpanded ? new Set(displayServicesInit.map(s => s.id)) : new Set()
  );

  // Filter services if filterIds provided (for contextual placement)
  const displayServices = displayServicesInit;

  const toggleRow = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <section className="transform-section" data-testid="service-transform-cards">
      <div className="transform-container">
        {!filterIds && (
          <h2 className="transform-section-title">The Difference, Service by Service</h2>
        )}
        <div className="transform-list">
          {displayServices.map((service) => {
            const isOpen = openIds.has(service.id);
            return (
              <div
                className={`transform-row ${isOpen ? 'is-open' : ''}`}
                key={service.id}
                data-testid={`transform-row-${service.id}`}
              >
                <button
                  type="button"
                  className="transform-head"
                  aria-expanded={isOpen}
                  aria-controls={`transform-body-${service.id}`}
                  onClick={() => toggleRow(service.id)}
                  data-testid={`transform-${service.id}`}
                >
                  <span className="transform-head-left">
                    <span className="transform-tag">{service.tag}</span>
                    <span className="transform-title">{service.title}</span>
                  </span>
                  <span className="transform-chev" aria-hidden="true">
                    <ChevronDown size={22} strokeWidth={2.5} />
                  </span>
                </button>
                <div
                  id={`transform-body-${service.id}`}
                  className="transform-body"
                  role="region"
                >
                  <div className="transform-body-inner">
                    <div className="transform-pair">
                      <div className="transform-side transform-side-prior">
                        <span className="transform-side-label">Your Prior Experience</span>
                        <ul className="transform-bullets">
                          {service.typical.map((item, idx) => (
                            <li key={idx}>
                              <span className="transform-ico transform-ico-prior">○</span>
                              <span className="transform-bullet-text">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="transform-side transform-side-promise">
                        <span className="transform-side-label">The CADENCE Promise</span>
                        <ul className="transform-bullets">
                          {service.promise.map((item, idx) => (
                            <li key={idx}>
                              <span className="transform-ico transform-ico-promise">✓</span>
                              <span className="transform-bullet-text">{item}</span>
                            </li>
                          ))}
                        </ul>
                        {service.link && (
                          <Link
                            to={service.link}
                            className="transform-learn-more"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Learn More →
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServiceTransformCards;
